import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Tags, Loader2, Building2, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RECORD_TYPES = [
  { value: 'care_provider', label: 'Care Provider' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'other', label: 'Other' },
];
export const SUPPLIER_TAGS = [
  { value: 'enquired', label: 'Enquired' },
  { value: 'free_listing', label: 'Free Listing' },
  { value: 'which_guide', label: "In a 'Which?' Guide" },
  { value: 'former_customer', label: 'Former Customer' },
  { value: 'current_customer', label: 'Current Customer' },
];
export const PROVIDER_TYPES = [
  { value: 'homecare', label: 'Homecare' },
  { value: 'care_home', label: 'Care Home' },
  { value: 'nursing_home', label: 'Nursing Home' },
  { value: 'supported_living', label: 'Supported Living' },
];
export const REGULATORS = [
  { value: 'cqc', label: 'CQC (England)' },
  { value: 'care_inspectorate', label: 'Care Inspectorate (Scotland)' },
  { value: 'ciw', label: 'CIW (Wales)' },
  { value: 'rqia', label: 'RQIA (Northern Ireland)' },
];
export const SIZE_BANDS = [
  { value: 'under_15', label: 'Under 15 locations' },
  { value: '15_49', label: '15-49 locations' },
  { value: '50_plus', label: '50+ locations' },
  { value: '100_plus', label: '100+ locations' },
];

const RECORD_TYPE_STYLE = {
  care_provider: 'border-emerald-700/40 text-emerald-400',
  supplier: 'border-sky-700/40 text-sky-400',
  other: 'border-slate-700 text-slate-400',
};
const labelFor = (list, val) => list.find(o => o.value === val)?.label || val;

export const RecordTypeBadge = ({ type }) => {
  if (!type) return null;
  const Icon = type === 'supplier' ? Truck : Building2;
  return (
    <Badge variant="outline" className={`text-[10px] shrink-0 ${RECORD_TYPE_STYLE[type] || 'border-slate-700 text-slate-400'}`} data-testid={`record-type-badge-${type}`}>
      <Icon className="w-3 h-3 mr-1" /> {labelFor(RECORD_TYPES, type)}
    </Badge>
  );
};

export const SupplierTagBadges = ({ tags }) => {
  if (!tags || !tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(t => (
        <Badge key={t} variant="outline" className="text-[10px] border-violet-700/40 text-violet-400" data-testid={`supplier-tag-badge-${t}`}>
          {labelFor(SUPPLIER_TAGS, t)}
        </Badge>
      ))}
    </div>
  );
};

const TagPill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active
      ? 'bg-violet-600 border-violet-500 text-white'
      : 'bg-slate-950 border-slate-700 text-slate-300 hover:border-violet-600/60'}`}
  >
    {children}
  </button>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-slate-300 text-xs">{label}</Label>
    <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? null : v)}>
      <SelectTrigger className="bg-slate-950 border-slate-800" data-testid={testid}><SelectValue placeholder="-" /></SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-800">
        <SelectItem value="none">- none -</SelectItem>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

/** Inline editor for a single company's care-segmentation (used in the detail dialog). */
export const CareSegmentEditor = ({ company, onSaved }) => {
  const [recordType, setRecordType] = useState(company.record_type || null);
  const [supplierTags, setSupplierTags] = useState(company.supplier_tags || []);
  const [providerType, setProviderType] = useState(company.provider_type || null);
  const [regulator, setRegulator] = useState(company.regulator || null);
  const [sizeBand, setSizeBand] = useState(company.size_band || null);
  const [saving, setSaving] = useState(false);

  const toggleTag = (t) => setSupplierTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/companies/${company.id}`, {
        record_type: recordType,
        supplier_tags: supplierTags,
        provider_type: providerType,
        regulator,
        size_band: sizeBand,
      });
      toast.success('Segmentation saved');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save segmentation');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 border border-slate-800 rounded-lg p-3" data-testid="care-segment-editor">
      <Label className="text-slate-300 flex items-center gap-2"><Tags className="w-4 h-4" /> Care segmentation</Label>
      <SelectField label="Record type" value={recordType} onChange={setRecordType} options={RECORD_TYPES} testid="cse-record-type" />

      {recordType === 'supplier' && (
        <div>
          <Label className="text-slate-300 text-xs mb-1.5 block">Supplier tags (multiple)</Label>
          <div className="flex flex-wrap gap-2">
            {SUPPLIER_TAGS.map(t => (
              <TagPill key={t.value} active={supplierTags.includes(t.value)} onClick={() => toggleTag(t.value)} testid={`cse-tag-${t.value}`}>
                {t.label}
              </TagPill>
            ))}
          </div>
        </div>
      )}

      {recordType === 'care_provider' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SelectField label="Provider type" value={providerType} onChange={setProviderType} options={PROVIDER_TYPES} testid="cse-provider-type" />
          <SelectField label="Regulator / country" value={regulator} onChange={setRegulator} options={REGULATORS} testid="cse-regulator" />
          <SelectField label="Size (locations)" value={sizeBand} onChange={setSizeBand} options={SIZE_BANDS} testid="cse-size-band" />
        </div>
      )}

      <Button size="sm" className="bg-violet-600 hover:bg-violet-500" onClick={save} disabled={saving} data-testid="cse-save-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Save segmentation'}
      </Button>
    </div>
  );
};

/** Bulk care-segmentation for many selected companies. */
export const CareSegmentBulkDialog = ({ ids, open, onOpenChange, onDone }) => {
  const [recordType, setRecordType] = useState(null);
  const [supplierTags, setSupplierTags] = useState([]);
  const [tagsMode, setTagsMode] = useState('set');
  const [providerType, setProviderType] = useState(null);
  const [regulator, setRegulator] = useState(null);
  const [sizeBand, setSizeBand] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRecordType(null); setSupplierTags([]); setTagsMode('set');
      setProviderType(null); setRegulator(null); setSizeBand(null);
    }
  }, [open]);

  const toggleTag = (t) => setSupplierTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const apply = async () => {
    const body = { company_ids: ids };
    if (recordType !== null) body.record_type = recordType;
    if (supplierTags.length) { body.supplier_tags = supplierTags; body.supplier_tags_mode = tagsMode; }
    if (providerType !== null) body.provider_type = providerType;
    if (regulator !== null) body.regulator = regulator;
    if (sizeBand !== null) body.size_band = sizeBand;
    if (Object.keys(body).length <= 1) { toast.error('Choose at least one field to apply'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/care-segments/bulk`, body);
      toast.success(`Updated ${res.data.updated} companies`);
      onOpenChange(false);
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="care-segment-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2"><Tags className="w-5 h-5 text-violet-400" /> Segment {ids.length} companies</DialogTitle>
          <DialogDescription className="text-slate-400">Apply care-sector segmentation to the selected companies. Blank fields are left unchanged.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <SelectField label="Record type" value={recordType} onChange={setRecordType} options={RECORD_TYPES} testid="bulk-record-type" />

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-slate-300 text-xs">Supplier tags</Label>
              <Select value={tagsMode} onValueChange={setTagsMode}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-7 w-[110px] text-xs" data-testid="bulk-tags-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="set">Replace</SelectItem>
                  <SelectItem value="add">Add</SelectItem>
                  <SelectItem value="remove">Remove</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              {SUPPLIER_TAGS.map(t => (
                <TagPill key={t.value} active={supplierTags.includes(t.value)} onClick={() => toggleTag(t.value)} testid={`bulk-tag-${t.value}`}>
                  {t.label}
                </TagPill>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SelectField label="Provider type" value={providerType} onChange={setProviderType} options={PROVIDER_TYPES} testid="bulk-provider-type" />
            <SelectField label="Regulator" value={regulator} onChange={setRegulator} options={REGULATORS} testid="bulk-regulator" />
            <SelectField label="Size (locations)" value={sizeBand} onChange={setSizeBand} options={SIZE_BANDS} testid="bulk-size-band" />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-500" onClick={apply} disabled={saving} data-testid="bulk-segment-apply-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Applying…</> : 'Apply to selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
